import { db } from "../../config/firebaseConfig";
import {
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  runTransaction,
  getDoc,
} from "firebase/firestore";
import { toast } from "react-hot-toast";

/**
 * Transition from WAITING to STAKING stage
 */
export const transitionToStaking = async (roomId, minPlayers) => {
  try {
    const roomRef = doc(db, "rooms", roomId);

    await runTransaction(db, async (transaction) => {
      const roomSnap = await transaction.get(roomRef);
      const roomData = roomSnap.data();

      // Verify minimum ready players
      const readyCount = roomData.readyPlayers?.length || 0;
      if (readyCount < minPlayers) {
        throw new Error("Not enough ready players to start staking");
      }

      // Calculate stage end time
      const stakingDuration = roomData.astraposConfig?.stakingDuration || 2; // minutes
      const endsAt = new Date(Date.now() + stakingDuration * 60 * 1000);

      transaction.update(roomRef, {
        "gameState.stage": "STAKING",
        "gameState.stageStartedAt": serverTimestamp(),
        "gameState.stageEndsAt": Timestamp.fromDate(endsAt),
      });
    });
  } catch (error) {
    console.error("Error transitioning to STAKING:", error);
    throw error;
  }
};

/**
 * Transition from STAKING to WINNER_DISPLAY stage
 */
export const transitionToWinnerDisplay = async (roomId, winner) => {
  try {
    const roomRef = doc(db, "rooms", roomId);

    const endsAt = new Date(Date.now() + 10 * 1000); // 10 seconds

    await updateDoc(roomRef, {
      "gameState.stage": "WINNER_DISPLAY",
      "gameState.stageStartedAt": serverTimestamp(),
      "gameState.stageEndsAt": Timestamp.fromDate(endsAt),
      stakingWinner: winner.publicKey,
      stakingWinnerTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error transitioning to WINNER_DISPLAY:", error);
    throw error;
  }
};

/**
 * Transition from WINNER_DISPLAY to PLAYING stage
 */
export const transitionToPlaying = async (roomId) => {
  try {
    const roomRef = doc(db, "rooms", roomId);

    await updateDoc(roomRef, {
      "gameState.stage": "PLAYING",
      "gameState.stageStartedAt": serverTimestamp(),
      "gameState.stageEndsAt": null,
      "gameState.blocksMinedThisCycle": 0,
    });
  } catch (error) {
    console.error("Error transitioning to PLAYING:", error);
    throw error;
  }
};

/**
 * Transition from PLAYING to LEADERBOARD stage
 */
export const transitionToLeaderboard = async (roomId) => {
  try {
    const roomRef = doc(db, "rooms", roomId);

    const endsAt = new Date(Date.now() + 60 * 1000); // 60 seconds

    await updateDoc(roomRef, {
      "gameState.stage": "LEADERBOARD",
      "gameState.stageStartedAt": serverTimestamp(),
      "gameState.stageEndsAt": Timestamp.fromDate(endsAt),
    });
  } catch (error) {
    console.error("Error transitioning to LEADERBOARD:", error);
    throw error;
  }
};

/**
 * Transition from LEADERBOARD to WAITING (reset cycle)
 */
export const transitionToWaiting = async (roomId) => {
  try {
    // Clear stale role from previous cycle to prevent privilege escalation
    localStorage.removeItem('role');

    const roomRef = doc(db, "rooms", roomId);

    await runTransaction(db, async (transaction) => {
      const roomSnap = await transaction.get(roomRef);
      const roomData = roomSnap.data();

      const newCycle = (roomData.gameState?.currentCycle || 0) + 1;

      transaction.update(roomRef, {
        "gameState.stage": "WAITING",
        "gameState.stageStartedAt": serverTimestamp(),
        "gameState.stageEndsAt": null,
        "gameState.currentCycle": newCycle,
        "gameState.blocksMinedThisCycle": 0,
        readyPlayers: [],
        stakingWinner: null,
        stakingWinnerTimestamp: null,
      });
    });
  } catch (error) {
    console.error("Error transitioning to WAITING:", error);
    throw error;
  }
};

/**
 * Check if a stage transition is needed (for client-side monitoring)
 * Returns the next stage if a transition should occur, or null if no transition needed
 */
export const checkStageTransition = (roomData) => {
  if (!roomData || !roomData.gameState) {
    return null;
  }

  const currentStage = roomData.gameState.stage;
  const now = Date.now();
  const endsAt = roomData.gameState.stageEndsAt
    ? roomData.gameState.stageEndsAt.toDate().getTime()
    : null;

  // Check if stage has expired
  if (endsAt && now >= endsAt) {
    switch (currentStage) {
      case "STAKING":
        return "WINNER_DISPLAY";
      case "WINNER_DISPLAY":
        return "PLAYING";
      case "LEADERBOARD":
        return "WAITING";
      default:
        return null;
    }
  }

  // Check if block count reached target
  if (currentStage === "PLAYING") {
    const TARGET_BLOCKS = 5;
    if (roomData.gameState.blocksMinedThisCycle >= TARGET_BLOCKS) {
      return "LEADERBOARD";
    }
  }

  return null;
};

/**
 * Perform the actual transition to the next stage
 */
export const performStageTransition = async (roomId, nextStage) => {
  try {
    const roomRef = doc(db, "rooms", roomId);

    switch (nextStage) {
      case "WINNER_DISPLAY":
        // This is handled by staking logic
        break;
      case "PLAYING":
        await transitionToPlaying(roomId);
        break;
      case "LEADERBOARD":
        await transitionToLeaderboard(roomId);
        break;
      case "WAITING":
        await transitionToWaiting(roomId);
        break;
      default:
        throw new Error(`Unknown stage: ${nextStage}`);
    }
  } catch (error) {
    console.error(`Error performing transition to ${nextStage}:`, error);
    throw error;
  }
};
